import { Component, EventEmitter, Input, Output, ViewChildren } from '@angular/core';
import { TicketContextService } from '@nx-fe/services';
import { Activity } from '../../../../../ticket/ticket.model';
import { ActivityComponent } from '../../activity/activity.component';
/** Defines the properties and functions bound by the component */
export interface IActivityListBindings {
  /** The root activity with children to list */
  activities: any;

  /** Invoked when an activity is to be updated/saved */
  onUpdate: any;

  /** Specifies whether the user can edit the activity */
  canEdit: any;

  /** Invoked when the activity list needs to be */
  onRefresh: any;
}

/** Defines the activity component - linking the controller, template, and bindings */
@Component({
  selector: 'xs-activity-list',
  templateUrl: './activity-list.component.html',
  styleUrls: ['./activity-list.component.scss'],
})
export class ActivityListComponent {
  public transclude: boolean;
  public bindings: { [boundProperty: string]: string };
  public controller: any;
  public controllerAs: string;
  public template: string;

  constructor(public ticketContextService: TicketContextService) {}
  /** See: IActivityListBindings.rootActivity */
  @Input()
  public activities: Activity[];

  /** See: IActivityListBindings.canEdit */
  @Input()
  public canEdit: boolean;

  @Input()
  public isSPPMode: boolean = false;

  /** See: IActivityBindings.onUpdate */
  @Output()
  public onUpdate: EventEmitter<any> = new EventEmitter();

  /** See: IActivityBindings.onRefreshActivityList  */
  @Output()
  public onRefresh: EventEmitter<any> = new EventEmitter();

  @Output() isEditting: EventEmitter<boolean> = new EventEmitter<boolean>();

  /** Counter for keeping track of already-used IDs */
  public idCounter = 1;

  @ViewChildren(ActivityComponent) activityList: ActivityComponent[];

  /** Deletes the given activity from the root activity's childActivities list */
  public onDelete = (activity: Activity) => {
    const activities = this.activities;
    if (activities) {
      const removeIdx = activities.indexOf(activity);
      if (removeIdx > -1) {
        this.onUpdate.emit();
        activities.splice(removeIdx, 1);
      }
    }
  };

  /** Generates a unique ID to represent the activity for the ng-repeat */
  public generateUniqueId = (activity: Activity): string => {
    // Only generate an ID if one is not already assigned
    if (!activity.$repeaterId) {
      activity.$repeaterId = 'generated_id_' + this.idCounter;
      this.idCounter++;
    }
    return activity.$repeaterId;
  };

  /** Indicates whether any activities are in the list */
  public hasActivities() {
    return !!this.activities && this.activities.length > 0;
  }

  /** Removes private activities if user is a reporter */
  public filterPrivateActivities() {
    const canAddPrivateNote = this.ticketContextService.canAddPrivateNote();
    const canViewPrivateNote = this.ticketContextService.canViewPrivateNote();
    const canViewResolutionNote = this.ticketContextService.canAddResolutionNote();

    for (const activity of this.activities) {
      if (!activity) {
        continue;
      }

      const isNew = !activity.activityId;
      const isNewResolutionNote = !activity.description && activity.details.isResolution;
      const shouldShowPrivateNote = isNew || isNewResolutionNote ? canAddPrivateNote : canViewPrivateNote;
      const removeItem =
        (!shouldShowPrivateNote && activity.details.isPrivate) || // private note
        (!canViewResolutionNote && activity.details.isResolution); // resolution note

      if (removeItem) {
        this.onDelete(activity);
      }
    }
  }

  /** Returns the activities for this ticket*/
  public getActivities() {
    this.filterPrivateActivities();

    return this.activities;
  }
}
